/**
 * Twee soorten voorkeur, bewust uit elkaar gehouden.
 *
 * `Kaartinstellingen` is wat het weergavepaneel naast de kaart bijstelt terwijl
 * je naar de kaart kijkt: wat de ballon zegt, welke vorm hij heeft, wat de kleur
 * betekent, welke lagen eronder liggen. `Voorkeuren` is wat je één keer zet en
 * onder Instellingen staat: de kaartstijl en de lagen waarmee de kaart opengaat.
 *
 * Beide staan in localStorage en niet in de URL: een gedeelde link draagt de
 * selectie, niet iemands smaak in ballongrootte.
 */
import {
  LABELFEED_DAGEN_MAX,
  LABELFEED_M2_MAX,
  LABELGROEPEN,
  labelgroepSchema,
  ZOEKFILTER_LABELS_STANDAARD,
} from '@mamimap/shared';
import { useCallback, useEffect, useState } from 'react';
import { z } from 'zod';
import { EENHEDEN } from './eenheden';

const SLEUTEL_KAART = 'mamimap.kaart';
const SLEUTEL_VOORKEUREN = 'mamimap.voorkeuren';
const SLEUTEL_RAIL = 'mamimap.rail';
const SLEUTEL_RONDVLUCHT = 'mamimap.rondvlucht';

export const KAARTSTIJLEN = ['grijs', 'standaard', 'pastel', 'luchtfoto'] as const;
export type Kaartstijl = (typeof KAARTSTIJLEN)[number];

/** De lagen die onder de woningpunten liggen, plus het clusteren zelf. */
export const lagenSchema = z.object({
  /** De woningen uit de vangst, de ballonnen. Uit laat alleen de andere lagen zien (MA-61). */
  woningen: z.boolean().default(true),
  clustering: z.boolean().default(true),
  percelen: z.boolean().default(false),
  luchtfoto: z.boolean().default(false),
  passanten: z.boolean().default(false),
  /** De bestemmingsvlakken uit het bestemmingsplan (MA-50). */
  bestemmingsplan: z.boolean().default(false),
  /** Nieuwe energielabels in het zoekgebied, als eigen pilletjes (MA-61). */
  labels: z.boolean().default(true),
  // Buurten en cijfers (MA-55) was hier even een schakelaar `buurten`. Die laag
  // is er nu altijd en verschijnt pas met de rechtermuisknop. Een oude waarde in
  // localStorage valt er stil af: z.object laat onbekende sleutels weg.
});
export type Lagen = z.infer<typeof lagenSchema>;

const LAGEN_STANDAARD: Lagen = {
  woningen: true,
  clustering: true,
  percelen: false,
  luchtfoto: false,
  passanten: false,
  bestemmingsplan: false,
  labels: true,
};

/** Grenzen van de schuifregelaar; 1 is de maat waarop de ballon ontworpen is. */
export const GROOTTE_MIN = 0.7;
export const GROOTTE_MAX = 1.6;

export const kaartinstellingenSchema = z.object({
  /** Wat er in de ballon staat; `prijsPerM2Label` is de ballon uit de brief. */
  ballon: z
    .enum(['prijsPerM2Label', 'prijsPerM2', 'vraagprijs', 'oppervlakte', 'prijsPerM2Oppervlakte'])
    .default('prijsPerM2Label'),
  vorm: z.enum(['pil', 'rond', 'druppel', 'blok']).default('pil'),
  /** Wat de kleur op de ballon betekent. */
  kleur: z
    .enum(['energielabel', 'prijsPerM2', 'bouwjaar', 'woningtype', 'geen'])
    .default('energielabel'),
  /** Het regeltje onder de ballon. */
  tekst: z.enum(['geen', 'straat', 'buurt', 'oppervlakte']).default('geen'),
  markeringen: z
    .object({
      nieuw: z.boolean().default(false),
      prijsverlaging: z.boolean().default(false),
      zonderLabel: z.boolean().default(false),
      zonderCoordinaat: z.boolean().default(false),
      /** Uit het aanbod geweest en teruggekomen (MA-22). */
      eerderActief: z.boolean().default(false),
    })
    .default({
      nieuw: false,
      prijsverlaging: false,
      zonderLabel: false,
      zonderCoordinaat: false,
      eerderActief: false,
    }),
  lagen: lagenSchema.default(LAGEN_STANDAARD),
  /** Hoeveel dagen terug de laag met nieuwe labels kijkt (MA-61). */
  labelsDagen: z.number().int().min(1).max(LABELFEED_DAGEN_MAX).default(30),
  /** Welke klassen de labellaag toont; standaard D t/m G, net als het zoekfilter. */
  labelsGroepen: z
    .array(labelgroepSchema)
    .max(LABELGROEPEN.length)
    .default([...ZOEKFILTER_LABELS_STANDAARD]),
  /** Oppervlakte volgens het label; null is geen grens. */
  labelsMinM2: z.number().int().min(0).max(LABELFEED_M2_MAX).nullable().default(null),
  labelsMaxM2: z.number().int().min(0).max(LABELFEED_M2_MAX).nullable().default(null),
  /** Labels van adressen die op Funda staan: weglaten, alleen die, of allebei. */
  labelsFunda: z.enum(['zonder', 'alleen', 'alle']).default('zonder'),
  grootte: z.number().min(GROOTTE_MIN).max(GROOTTE_MAX).default(1),
  stijl: z.enum(KAARTSTIJLEN).default('grijs'),
});

export type Kaartinstellingen = z.infer<typeof kaartinstellingenSchema>;

/** App-brede voorkeuren: waarmee de kaart opengaat, en waar 'standaard' naar terugvalt. */
export const voorkeurenSchema = z.object({
  stijl: z.enum(KAARTSTIJLEN).default('grijs'),
  lagen: lagenSchema.default(LAGEN_STANDAARD),
  /**
   * Waarin oppervlakte en prijs per oppervlakte op het scherm staan. Het filter
   * in de URL blijft metriek, zodat een gedeelde weergave bij iedereen dezelfde
   * woningen oplevert.
   */
  eenheid: z.enum(EENHEDEN).default('m2'),
});
export type Voorkeuren = z.infer<typeof voorkeurenSchema>;

export const VOORKEUREN_STANDAARD: Voorkeuren = voorkeurenSchema.parse({});

/**
 * De standaardstand van het paneel. Stijl en lagen komen uit de app-brede
 * voorkeuren: 'terug naar standaard' brengt je terug bij wat je onder
 * Instellingen hebt gezet, niet bij wat wij ooit hebben bedacht.
 */
export function standaardKaartinstellingen(voorkeuren: Voorkeuren): Kaartinstellingen {
  return {
    ...kaartinstellingenSchema.parse({}),
    stijl: voorkeuren.stijl,
    lagen: { ...voorkeuren.lagen },
  };
}

function lees<T>(sleutel: string, schema: z.ZodType<T>, terugval: T): T {
  try {
    const ruw = window.localStorage.getItem(sleutel);
    if (!ruw) return terugval;
    const uitkomst = schema.safeParse(JSON.parse(ruw));
    return uitkomst.success ? uitkomst.data : terugval;
  } catch {
    return terugval;
  }
}

function schrijf(sleutel: string, waarde: unknown): void {
  try {
    window.localStorage.setItem(sleutel, JSON.stringify(waarde));
  } catch {
    // Privémodus of een volle quota: alles werkt nog, het onthoudt alleen niets.
  }
}

export function useVoorkeuren(): [Voorkeuren, (wijziging: Partial<Voorkeuren>) => void] {
  const [voorkeuren, zetVoorkeuren] = useState<Voorkeuren>(() =>
    lees(SLEUTEL_VOORKEUREN, voorkeurenSchema, VOORKEUREN_STANDAARD),
  );

  useEffect(() => {
    schrijf(SLEUTEL_VOORKEUREN, voorkeuren);
  }, [voorkeuren]);

  const zet = useCallback((wijziging: Partial<Voorkeuren>) => {
    zetVoorkeuren((vorige) => ({ ...vorige, ...wijziging }));
  }, []);

  return [voorkeuren, zet];
}

export type KaartinstellingenWijziging = Partial<
  Omit<Kaartinstellingen, 'markeringen' | 'lagen'>
> & {
  markeringen?: Partial<Kaartinstellingen['markeringen']>;
  lagen?: Partial<Lagen>;
};

export function useKaartinstellingen(voorkeuren: Voorkeuren): {
  instellingen: Kaartinstellingen;
  zet: (wijziging: KaartinstellingenWijziging) => void;
  herstel: () => void;
  isStandaard: boolean;
} {
  const standaard = standaardKaartinstellingen(voorkeuren);
  const [instellingen, zetInstellingen] = useState<Kaartinstellingen>(() =>
    lees(SLEUTEL_KAART, kaartinstellingenSchema, standaardKaartinstellingen(VOORKEUREN_STANDAARD)),
  );

  useEffect(() => {
    schrijf(SLEUTEL_KAART, instellingen);
  }, [instellingen]);

  const zet = useCallback((wijziging: KaartinstellingenWijziging) => {
    zetInstellingen((vorige) => ({
      ...vorige,
      ...wijziging,
      markeringen: { ...vorige.markeringen, ...wijziging.markeringen },
      lagen: { ...vorige.lagen, ...wijziging.lagen },
    }));
  }, []);

  const herstel = useCallback(() => {
    zetInstellingen(standaardKaartinstellingen(voorkeuren));
  }, [voorkeuren]);

  return {
    instellingen,
    zet,
    herstel,
    isStandaard: JSON.stringify(instellingen) === JSON.stringify(standaard),
  };
}

/**
 * De stand van de rail links: ingeklapt of uitgeklapt, onthouden tussen bezoeken.
 * Is er nog niets onthouden, dan begint hij op een laptopscherm ingeklapt - daar
 * zouden rail en weergavepaneel samen te veel van de kaart afsnoepen.
 */
const RAIL_UIT_ONDER = 1440;

export function useRail(): [boolean, (open: boolean) => void] {
  const [open, zetOpen] = useState<boolean>(() =>
    lees(SLEUTEL_RAIL, z.boolean(), window.innerWidth >= RAIL_UIT_ONDER),
  );

  useEffect(() => {
    schrijf(SLEUTEL_RAIL, open);
  }, [open]);

  return [open, zetOpen];
}

/**
 * Rondvlucht in 3D (MA-38): aan of uit, onthouden tussen bezoeken. Een stand
 * en geen handeling: aan betekent dat de camera om het middelpunt cirkelt zodra
 * ze stilstaat, ook na het kiezen van een andere woning. Standaard uit.
 */
export function useRondvlucht(): [boolean, (aan: boolean) => void] {
  const [aan, zetAan] = useState<boolean>(() => lees(SLEUTEL_RONDVLUCHT, z.boolean(), false));

  useEffect(() => {
    schrijf(SLEUTEL_RONDVLUCHT, aan);
  }, [aan]);

  return [aan, zetAan];
}
